import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import jsPDF from 'jspdf';
import { BCAReports, BPTable, CCEDewPointReports, CCEReports, CVDReportData, DLEReportData, DPTable, FractionUpdate, LabTestReportData, MSSTReportData, ParaffinReports, SummaryOfCCEReports, viscosityReports } from '../../models/lab-test';
import { FontService } from '../FontService';
import { RecombinationQCReportData } from '../../recombination-qc/models/recombination-qc';
import { DecontaminationReportData } from '../../liveoil-decontamination/models/decontamination';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { SwellingFinalReport, Vw_SwellReportData } from '../../swell/models/swelling';
import { ProjectService } from '../project.service';
import { CommonBodyStyles, CommonBodyStylesOthers, CommonBodyStyleWithBorder, CommonHeadStyles, CommonHeadStylesOthers, CommonStyles, CommonStylesOthers, FractionReports, SampleReport, SeparatorGasReports, SeparatorOilReports } from '../../models/project';
import { FinalReportDetailsModel } from '../../models/sample';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { ReportPDFRenderersService } from './report.pdfRenderers.service';
@Injectable({
    providedIn: 'root'
})
export class ReportPDFBuildersService implements OnDestroy {
    private fontService = inject(FontService);
    private projectService = inject(ProjectService);
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);
    private reportPDFRenderersService = inject(ReportPDFRenderersService);
    private readonly destroy$ = new Subject<void>();
    /*New PDF Report Start*/

    commonStyles: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    commonHeadStyles: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStyles: CommonBodyStyles = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesOthers: CommonStylesOthers = {
        fontSize: 10,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    }

    commonHeadStylesOthers: CommonHeadStylesOthers = {
        fontSize: 12,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStylesOthers: CommonBodyStylesOthers = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesWithoutBorder: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };
    commonHeadStylesWithoutBorder: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0 // Remove border
    };
    commonBodyStylesWithoutBorder: CommonBodyStyleWithBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    }





    /*New PDF Report End*/
    async GeneratePdf(labTestData: LabTestReportData, reportType: string, preview: boolean): Promise<Blob | void> {
        const labTestId = labTestData.dataLabTest[0]?.labTestId;

        if (!labTestId) {
            console.error(reportConstantsMessage.labTestIdMissingInDataLabTest);
            return;
        }
        const bcaExp: BCAReports[] = labTestData.expResults.filter((x): x is BCAReports => x.experimentName === reportConstants.experimentNameBCA);
        const msstExp: MSSTReportData[] = labTestData.expResults.filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST);
        const paraffinExp = labTestData.expResults.filter((x): x is ParaffinReports => x.experimentName === reportConstants.experimentNameParaffin) ?? null;
        const viscosityExps: viscosityReports[] = labTestData.expResults.filter((x): x is viscosityReports => x.experimentName === reportConstants.experimentNameViscosity);
        const cceDewPoint: CCEDewPointReports[] = labTestData.expResults.filter((x): x is CCEDewPointReports => x.experimentName === reportConstants.experimentNameCCEDewPoint);
        const fractionExp: FractionReports = labTestData.expResults.filter((x): x is FractionReports => x.experimentName === reportConstants.experimentNameFraction)[0] ?? null;
        const recombinationQC: RecombinationQCReportData[] = labTestData.expResults.filter((x): x is RecombinationQCReportData => x.experimentName === reportConstants.experimentNameRecombination) ?? null;
        const decontamination: DecontaminationReportData[] = labTestData.expResults.filter((x): x is DecontaminationReportData => x.experimentName === reportConstants.experimentNameDecontamination) ?? null;
        const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE) ?? null;
        const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint) ?? null;
        const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD);
        const swellingExp: SwellingFinalReport[] = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling) ?? null;
        const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE);
        const sampleDetailsWrap: FractionUpdate | null = labTestData.expResults.find((x): x is FractionUpdate => x.experimentName === reportConstants.experimentNameSampleDetails) ?? null;;
        const sampleDetailsExp: SampleReport = sampleDetailsWrap?.data ?? {} as SampleReport;;
        const separatorGasComp: SeparatorGasReports[] = labTestData.expResults.filter((x): x is SeparatorGasReports => x.experimentName === reportConstants.experimentNameSeparatorGas);
        const listOfCCE = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE);

        const separatorOilComp: SeparatorOilReports[] = labTestData.expResults.filter((x): x is SeparatorOilReports => x.experimentName === reportConstants.experimentNameSeparatorOil);
        const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p', compress: true });
        doc.setLineCap('butt');
        doc.setLineJoin('miter');
        const fontSizeTitle = 20;
        const fontSizeSubTitle = 18;
        const margin = 25;
        this.fontService.fonts.forEach(font => {
            doc.addFileToVFS(font.ttfName, font.base64);
            doc.addFont(font.ttfName, font.fontName, font.fontWeight);
        });
        const meta = this._reportGeneralUtilityService.GetFinalReportNamesNew(sampleDetailsExp.finalReportDetails);
        const reportContent = this._reportGeneralUtilityService.SelectReportContent(reportType, labTestData, sampleDetailsExp, sampleDetailsExp.finalReportDetails);
        const addressToName = meta.reportToPrefix ? `${meta.reportToPrefix}. ${meta.toName}` : meta.toName;
        const dearName = meta.toName;
        const pageLogoDataUrl = await this._reportGeneralUtilityService.getImageDataUrl(reportConstants.pageLogoName);
        const frontOfficeDataUrl = await this._reportGeneralUtilityService.getImageDataUrl(reportConstants.frontpageOffice);
        const coverLogoDataUrl = await this._reportGeneralUtilityService.getImageDataUrl(reportConstants.coverLogo);
        this.reportPDFRenderersService.FrontPage(doc, margin, reportContent.wellDetails, meta.frontPageNames, pageLogoDataUrl, frontOfficeDataUrl);
        this.reportPDFRenderersService.CoverPage(doc, margin, reportContent.coverData, addressToName, meta.fromName, reportContent.coverContent, dearName, sampleDetailsExp.finalReportDetails, coverLogoDataUrl);
        this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.summaryOfAnalysesMethods, "", fontSizeTitle, fontSizeSubTitle);
        let index = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfAnalysisMethods);
        if (index > -1) {
            reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
        } else {
            const index = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfAnalysesMethods);
            if (index > -1) {
                reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
            }
        }
        this.reportPDFRenderersService.SummaryOfAnalyses(doc, reportContent.sampleAnalyses, margin);
        this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.reservoirWellAndSamplingInformation, "", fontSizeTitle, fontSizeSubTitle);
        index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.samplingInformation));
        if (index > -1) {
            reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
        }

        this.reportPDFRenderersService.WellAndSamplingInformation(doc, margin, reportContent.wellInformation, reportContent.sampleInformation, reportContent.sampleInformationSeparator, reportType);

        this.reportPDFRenderersService.WellAndSamplingDetails(doc, margin, reportContent.sampleInformationDetails, reportContent.reservoirProperties, reportContent.reservoirPropertiesDST, reportContent.saturatedProperties, reportContent.sampleInformationDetailsSeparator, reportContent.reservoirPropertiesSeparator, reportContent.wellInformation, reportType);

        const sampleName = reportContent?.sampleInformationDetails[0]?.sampleId !== undefined ? reportContent?.sampleInformationDetails[0]?.sampleId : '';
        let subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleName;

        const wellName = reportContent?.wellInformation.well !== undefined ? reportContent.wellInformation.well : '';
        const wellNameVersion = reportContent.wellInformation.well;
        let sampleNameVersion = reportConstants.sample + ' ' + sampleName;
        /*Compositional Analysis*/

        if (reportType === reportConstants.reportTypeMDT || reportType === reportConstants.reportTypeDST) {
            const sampleList = sampleDetailsExp.samplingInformation?.rows ?? [];
            /*Compositional Analysis*/
            sampleList.forEach((element) => {
                const sampleID = element.sampleNumber;
                const bcaExp: BCAReports[] = labTestData.expResults
                    .filter((x): x is BCAReports => x.experimentName === reportConstants.experimentNameBCA && x.sampleIdentifier?.toLowerCase() === element.sampleIdentifier?.toLowerCase()) ?? null;

                const decontamination = labTestData.expResults.filter((x): x is DecontaminationReportData => x.experimentName === reportConstants.experimentNameDecontamination && x.sampleIdentifier?.toLowerCase() === element.sampleIdentifier?.toLowerCase()) ?? null;
                const paraffinExp: ParaffinReports[] = labTestData.expResults.filter((x): x is ParaffinReports => x.experimentName === reportConstants.experimentNameParaffin && x.sampleIdentifier?.toLowerCase() === element.sampleIdentifier?.toLowerCase()) ?? null;
                const recombinationQC = labTestData.expResults.filter((x): x is RecombinationQCReportData => x.experimentName === reportConstants.experimentNameRecombination && x.sampleIdentifier?.toLowerCase() === element.sampleIdentifier?.toLowerCase()) ?? null;
                if (bcaExp.length > 0 || recombinationQC.length > 0 || decontamination.length > 0 || paraffinExp.length > 0) {
                    subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleID + ' (' + element.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    sampleNameVersion = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleID;
                    this.reportPDFRenderersService.AddCenteredSection(doc, ' ' + reportConstants.compositionalAnalysis, subContent, fontSizeTitle, fontSizeSubTitle);

                    const pageBCA = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleID + " (" + element.sampleDepthPoint + " " + reportConstants.mMD + ")"
                    const findBCASummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.compositionalAnalysis);
                    const index = reportContent.tableItem.findIndex(
                        (x, i) => i > findBCASummaryIndex && x.title === pageBCA
                    );
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                }
                /*BCA*/
                bcaExp.forEach(element => {
                    if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === false) {
                        this.reportPDFRenderersService.CompositionalAnalysis(doc, margin, element, wellNameVersion, sampleNameVersion);
                    }
                });
                /*nParaffin*/
                if (paraffinExp !== null) {
                    /*n-Paraffin Analysis*/
                    const sampleNameVersionNP = reportConstants.sample + ' ' + element.sampleNumber + ' ' + reportType.toUpperCase();
                    paraffinExp.forEach(async element => {
                        this.reportPDFRenderersService.ParaffinPage(doc, margin, element, "", wellNameVersion, sampleNameVersionNP);
                    });
                }
                /*LiveOil_Decontamination*/
                decontamination.forEach(element => {
                    this.reportPDFRenderersService.Decontamination(doc, margin, element, wellNameVersion, sampleNameVersion);
                });

                /*Recombined Fluid*/
                const wellNameRecombined = reportContent.wellInformation.well;
                const sampleNameRecombined = reportConstants.recombinedFluid;
                bcaExp.forEach(element => {
                    if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === true) {
                        this.reportPDFRenderersService.CompositionalAnalysis(doc, margin, element, wellNameRecombined, sampleNameRecombined);
                    }
                });
                /*Recombined QC*/
                recombinationQC.forEach(element => {
                    this.reportPDFRenderersService.RecombinationQC(doc, margin, element, wellNameVersion);
                });
            });

            /*Summary of CCE Experiments- CCE and CCE-DewPoint*/
            sampleList.forEach((sampleElement) => {
                const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.summaryOfConstantCompositionExpansionExperiments, subContent, fontSizeTitle, fontSizeSubTitle);
                    const pageCCE = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                    const findCCESummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfCCEExperiments);
                    const index = reportContent.tableItem.findIndex(
                        (x, i) => i > findCCESummaryIndex && x.title === pageCCE
                    );

                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    //CCE
                    summaryOfCCE.forEach(async element => {
                        this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, sampleV);
                    });
                    //CCE Dew Point
                    summaryOfCCEDew.forEach(async element => {
                        this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, sampleV);
                    });
                }
            });
            /*Separator Test*/
            sampleList.forEach((sampleElement) => {
                const msstExp: MSSTReportData[] = labTestData.expResults
                    .filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase());
                const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                if (msstExp.length > 0) {
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.separatorTest, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title === reportConstants.separatorTests + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    msstExp.forEach(async element => {
                        this.reportPDFRenderersService.SeparatorTest(doc, margin, element, "", wellNameVersion, sampleV);
                    });
                }
            });
            /*Differential Liberation*/
            sampleList.forEach((sampleElement) => {
                const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                if (dleExp.length > 0) {
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.differentialLiberation, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title === reportConstants.differentialLiberation + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    dleExp.forEach(async element => {
                        this.reportPDFRenderersService.DiffLibTest(doc, margin, element, "", wellNameVersion, sampleV);
                    });
                }
            });
            /*Constant Volume Depletion (CVD)*/
            sampleList.forEach((sampleElement) => {
                const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                if (cvdExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.constantVolumeDepletion, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title === reportConstants.cvdExperiment + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    cvdExp.forEach(async element => {
                        this.reportPDFRenderersService.CVDStage(doc, margin, element.cvdData.cvdStageResult, "", wellNameVersion, sampleV);
                    });
                }
            });
            /*Viscosity*/
            sampleList.forEach((sampleElement) => {
                const viscosityExps: viscosityReports[] = labTestData.expResults.filter((x): x is viscosityReports => x.experimentName === reportConstants.experimentNameViscosity
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                if (viscosityExps.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.experimentNameViscosity, subContent, fontSizeTitle, fontSizeSubTitle);

                    const pageViscosity = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                    const findViscositySummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.experimentNameViscosity);
                    const index = reportContent.tableItem.findIndex(
                        (x, i) => i > findViscositySummaryIndex && x.title === pageViscosity
                    );
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    viscosityExps.forEach(async element => {
                        this.reportPDFRenderersService.GenerateViscosityPage(doc, margin, element, "", wellNameVersion, sampleV);
                    });
                }
            });

            /*Swelling*/
            sampleList.forEach((sampleElement) => {
                const swellingExp = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                ) ?? null;
                if (swellingExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.summaryOfSwellingTest, subContent, fontSizeTitle, fontSizeSubTitle);
                    const pageSwelling = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                    const findSwellingSummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfSwellingTest);
                    const index = reportContent.tableItem.findIndex(
                        (x, i) => i > findSwellingSummaryIndex && x.title === pageSwelling
                    );
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    swellingExp.forEach(element => {
                        this.reportPDFRenderersService.SwellingReport(doc, margin, element, wellNameVersion + ' ' + sampleV);
                        this.reportPDFRenderersService.SwellingGasCompDataReport(doc, margin, element, wellNameVersion + ' ' + sampleV);
                        this.reportPDFRenderersService.SwellingCalculatedCompBalData(doc, margin, element, wellNameVersion + ' ' + sampleV);
                    });
                }
            });

            /*Appendix*/
            const appendixData = reportContent.tableItem
                .map(x => { return x })
                .filter(x => x.title.includes(reportConstants.appendix));

            /*Appendix A: Fraction Reference Values*/
            const countOfFractionExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameFraction);
            if (countOfFractionExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, countOfFractionExp[0].title, subContent, fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === countOfFractionExp[0].title);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                this.reportPDFRenderersService.FractionAppendix(doc, margin, fractionExp);
            }
            /*Appendix B: CCE Data*/
            const sampleListCCE = labTestData.expResults.filter(x => x.experimentName === reportConstants.experimentNameCCE || x.experimentName === reportConstants.experimentNameCCEDewPoint).map(x => x.sampleIdentifier);
            sampleList.filter(x => sampleListCCE.includes(x.sampleIdentifier)).forEach((sampleElement) => {
                const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                const listOfCCE = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase());
                const cceDewPoint: CCEDewPointReports[] = labTestData.expResults.filter((x): x is CCEDewPointReports => x.experimentName === reportConstants.experimentNameCCEDewPoint && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase());
                const countOfCCEExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameCCE);

                if (countOfCCEExp.length > 0 || summaryOfCCEDew.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const appendixCCE = countOfCCEExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                    this.reportPDFRenderersService.AddCenteredSection(doc, (appendixCCE?.title.split(":")[0] ?? '') + "-" + reportConstants.CCEData, subContent, fontSizeTitle, fontSizeSubTitle);

                    index = reportContent.tableItem.findIndex(x => x.title.includes(appendixCCE?.title ?? ''));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    /*Summary of CCE */
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    summaryOfCCE.forEach(async element => {
                        this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, sampleV, appendixCCE?.title.split(reportConstants.CCEData)[0]);
                    });
                    /**********************************************************/
                    listOfCCE.forEach(exp => {
                        if (exp.cceProperties.isSwelling === false) {
                            this.reportPDFRenderersService.CCE(doc, margin, exp, wellNameVersion, sampleV);
                        }
                    });

                    /*****************************Dew Point*****************************/
                    summaryOfCCEDew.forEach(async element => {
                        this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, sampleV, appendixCCE?.title.split(reportConstants.CCEData)[0]);
                    });
                    cceDewPoint.forEach(exp => {
                        if (exp.cceDewPointProperties.isSwelling === false) {
                            this.reportPDFRenderersService.CCEDewPoint(doc, margin, exp, wellNameVersion, sampleV);
                        }
                    });
                }
            });

            /*Appendix E- Swelling Test*/
            const sampleListSwelling = labTestData.expResults.filter(x => x.experimentName === reportConstants.experimentNameSwelling).map(x => x.sampleIdentifier);
            sampleList.filter(x => sampleListSwelling.includes(x.sampleIdentifier)).forEach((sampleElement) => {
                const swellingExp: SwellingFinalReport[] = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                ) ?? null;
                const listOfCCE: CCEReports[] = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                const cceDewPoint: CCEDewPointReports[] = labTestData.expResults.filter((x): x is CCEDewPointReports => x.experimentName === reportConstants.experimentNameCCEDewPoint && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase());
                const countOfSSwellingExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameSwelling);
                if (countOfSSwellingExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const appendixSwelling = countOfSSwellingExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));

                    this.reportPDFRenderersService.AddCenteredSection(doc, (appendixSwelling?.title.split(":")[0] ?? '') + '-' + reportConstants.swellingTest.replace('-', ''), subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(appendixSwelling?.title ?? ''));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    swellingExp.forEach(element => {
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        this.reportPDFRenderersService.SwellingReport(doc, margin, element, wellNameVersion + ' ' + sampleV,appendixSwelling?.title.split(reportConstants.swellTest + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')')[0]);


                        listOfCCE.forEach(exp => {
                            if (exp.cceProperties.isSwelling === true && exp.cceProperties.swellingSummaryId === element.labRepeatId) {
                                const cceSatP = exp.bpTable.find((x: BPTable) => x.pointName === reportConstants.satP)?.pressure;
                                const temp = exp.cceProperties.studyTemperature;
                                const swellingSatP = (element.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;
                                this.reportPDFRenderersService.CCESepGas(doc, margin, exp, wellName, swellingSatP);
                            }
                        });

                        cceDewPoint.forEach(exp => {
                            if (exp.cceDewPointProperties.isSwelling === true && exp.cceDewPointProperties.swellingSummaryId === element.labRepeatId) {
                                const cceSatP = exp.dpTable.find((x: DPTable) => x.pointName === reportConstants.pSat)?.pressure;
                                const temp = exp.cceDewPointProperties.studyTemperature;
                                const swellingSatP = (element.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;
                                const molPercentage = parseFloat(swellingSatP.toFixed(2));
                                this.reportPDFRenderersService.CCEDewPointSepGas(doc, margin, exp, wellName, molPercentage);
                            }
                        });

                    });
                }
            });

            /*Appendix C: Separator Test Material Balance*/
            const sampleListSeparator = labTestData.expResults.filter(x => x.experimentName === reportConstants.experimentNameMSST).map(x => x.sampleIdentifier);
            sampleList.filter(x => sampleListSeparator.includes(x.sampleIdentifier)).forEach((sampleElement) => {
                const msstExp: MSSTReportData[] = labTestData.expResults.filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                const countOfMSSTExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameMSST);
                if (countOfMSSTExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const appendixMSST = countOfMSSTExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));

                    this.reportPDFRenderersService.AddCenteredSection(doc, (appendixMSST?.title.split(":")[0] ?? '') + "-" + reportConstants.separatorTest, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title === appendixMSST?.title);
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    msstExp.forEach(element => {
                        this.reportPDFRenderersService.SeparatorTest(doc, margin, element, appendixMSST?.title.split(reportConstants.separatorTests)[0] + " ", wellNameVersion, sampleV);
                        this.reportPDFRenderersService.SeparatorTestAppendix(doc, margin, element);
                    });
                }
            });
            /*Appendix -Diff Lib Material Balance*/
            const sampleListDL = labTestData.expResults.filter(x => x.experimentName === reportConstants.experimentNameDLE).map(x => x.sampleIdentifier);
            sampleList.filter(x => sampleListDL.includes(x.sampleIdentifier)).forEach((sampleElement) => {
                const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                const countOfDLEExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameDLE);
                if (countOfDLEExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const appendixDLE = countOfDLEExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                    this.reportPDFRenderersService.AddCenteredSection(doc, (appendixDLE?.title.split(":")[0] ?? '') + "-" + reportConstants.differentialLiberation, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title === appendixDLE?.title);
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    dleExp.forEach(element => {
                        this.reportPDFRenderersService.DiffLibTest(doc, margin, element, appendixDLE?.title.split(reportConstants.differentialLiberation)[0], wellNameVersion, sampleV);
                        this.reportPDFRenderersService.DiffLibTestAppendix(doc, margin, element);
                    });
                }
            });

            /*Appendix D-CVD*/
            const sampleListCVD = labTestData.expResults.filter(x => x.experimentName === reportConstants.experimentNameCVD).map(x => x.sampleIdentifier);
            sampleList.filter(x => sampleListCVD.includes(x.sampleIdentifier)).forEach((sampleElement) => {
                const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD
                    && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                );
                const countOfCVDExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameCVD);
                if (countOfCVDExp.length > 0) {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const appendixCVD = countOfCVDExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                    this.reportPDFRenderersService.AddCenteredSection(doc, (appendixCVD?.title.split(":")[0] ?? '') + '-' + reportConstants.constantVolumeDepletion, subContent, fontSizeTitle, fontSizeSubTitle);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(appendixCVD?.title ?? ''));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                    const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                    cvdExp.forEach(async element => {
                        this.reportPDFRenderersService.CVDStage(doc, margin, element.cvdData.cvdStageResult, appendixCVD?.title.split(reportConstants.cvdExperiment)[0], wellNameVersion, sampleV);
                        this.reportPDFRenderersService.CVDOtherPages(doc, margin, element.cvdData, wellNameVersion, sampleV);
                    });
                }
            });

        }
        if (reportType === reportConstants.reportTypeSeparatorTest) {
            const samples = sampleDetailsExp.samplingInformation?.rows.filter(x => x.isRecombine == true);
            const finalOutput = samples?.map(s => s.bottleNumber).join('-');
            const recombinedSampleNameVersion = reportConstants.sample + ' ' + finalOutput;
            const sampleName = reportConstants.experimentNameSeparatorOil;
            if (bcaExp.length > 0 || separatorGasComp.length > 0 || separatorOilComp.length > 0 || recombinationQC.length > 0 || decontamination.length > 0 || paraffinExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, ' ' + reportConstants.compositionalAnalysis, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.sampleCompositionalAnalysis);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
            }
            /*Sep Oil*/
            separatorOilComp.forEach(el => {
                this.reportPDFRenderersService.GenerateSeparatorOilPdfReport(doc, margin, el, wellName, sampleName);
            });
            /*n-Paraffin Analysis*/
            if (paraffinExp !== null) {
                paraffinExp.forEach(async element => {
                    let sampleNumber = sampleDetailsExp?.rows?.find(x => x.isRecombine == true && (x.separatorOilGas == reportConstants.sepDeadOil || x.separatorOilGas == reportConstants.sepOil))?.bottleNumber ?? "";
                    sampleNumber = reportConstants.sample + ' ' + sampleNumber;
                    this.reportPDFRenderersService.ParaffinPage(doc, margin, element, "", wellNameVersion, sampleNumber);
                });
            }
            /*Sep Gas*/
            separatorGasComp.forEach(el => {
                this.reportPDFRenderersService.GenerateSeparatorGasPdfReport(doc, margin, el);
            });

            /*BCA*/
            bcaExp.forEach(element => {
                if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === false) {
                    this.reportPDFRenderersService.CompositionalAnalysis(doc, margin, element, wellNameVersion, recombinedSampleNameVersion);
                }
            });
            /*Recombined BCA*/
            const wellNameRecombined = reportContent.wellInformation.well;
            const sampleNameRecombined = reportConstants.recombinedFluid;
            bcaExp.forEach(element => {
                if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === true) {
                    this.reportPDFRenderersService.CompositionalAnalysis(doc, margin, element, wellNameRecombined, sampleNameRecombined);
                }
            });
            /*Recombined QC*/
            recombinationQC.forEach(element => {
                this.reportPDFRenderersService.RecombinationQC(doc, margin, element, wellNameVersion);
            });
            /*LiveOil_Decontamination*/
            decontamination.forEach(element => {

                this.reportPDFRenderersService.Decontamination(doc, margin, element, wellNameVersion, recombinedSampleNameVersion);

            });

            /*Summary of CCE Experiments- CCE and CCE-DewPoint*/
            if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.summaryOfConstantCompositionExpansionExperiments, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfCCEExperiments);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
            }
            //CCE
            summaryOfCCE.forEach(async element => {
                this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, recombinedSampleNameVersion);
            });
            //CCE Dew Point
            summaryOfCCEDew.forEach(async element => {
                this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, recombinedSampleNameVersion);
            });

            /*Separator Test*/
            if (msstExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.separatorTest, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.separatorTest);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                } else {
                    index = reportContent.tableItem.findIndex(x => x.title === reportConstants.separatorTests);
                    if (index > -1) {
                        reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                    }
                }
                msstExp.forEach(async element => {
                    this.reportPDFRenderersService.SeparatorTest(doc, margin, element, "", "", "");
                });
            }

            if (dleExp.length > 0) {
                /*Differential Liberation*/
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.differentialLiberation, subContent, fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.differentialLiberation);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                dleExp.forEach(async element => {
                    this.reportPDFRenderersService.DiffLibTest(doc, margin, element, "", wellNameVersion, recombinedSampleNameVersion);
                });
            }

            /*Constant Volume Depletion (CVD)*/
            if (cvdExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.constantVolumeDepletion, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.cvdExperiment);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                cvdExp.forEach(async element => {
                    this.reportPDFRenderersService.CVDStage(doc, margin, element.cvdData.cvdStageResult, "", wellNameVersion, recombinedSampleNameVersion);
                });
            }

            /*Viscosity*/
            if (viscosityExps.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.experimentNameViscosity, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.experimentNameViscosity);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                viscosityExps.forEach(async element => {
                    this.reportPDFRenderersService.GenerateViscosityPage(doc, margin, element, "", wellNameVersion, recombinedSampleNameVersion);
                });
            }

            /*Swelling*/
            if (swellingExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, reportConstants.summaryOfSwellingTestExperiments, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfSwellingTestSepGas);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                swellingExp.forEach(element => {

                    this.reportPDFRenderersService.SwellingReport(doc, margin, element, wellNameVersion);
                    this.reportPDFRenderersService.SwellingGasCompDataReport(doc, margin, element, wellNameVersion);
                    this.reportPDFRenderersService.SwellingCalculatedCompBalData(doc, margin, element, wellNameVersion);

                });
            }
            /*Appendix*/
            const appendixData = reportContent.tableItem.map(x => { return x }).filter(x => x.title.includes(reportConstants.appendix));

            /*Appendix A: Fraction Reference Values*/
            const countOfFractionExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameFraction);
            if (countOfFractionExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, countOfFractionExp[0].title, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === countOfFractionExp[0].title);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                this.reportPDFRenderersService.FractionAppendix(doc, margin, fractionExp);
            }
            /*Appendix B: CCE Data*/
            const countOfCCEExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameCCE);

            if (countOfCCEExp.length > 0 || summaryOfCCEDew.length > 0) {

                this.reportPDFRenderersService.AddCenteredSection(doc, countOfCCEExp[0].title.replace(/(CCE Data).*/, reportConstants.dollar1), "", fontSizeTitle, fontSizeSubTitle);

                index = reportContent.tableItem.findIndex(x => x.title.includes(countOfCCEExp[0].title));
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                /*Summary of CCE */
                summaryOfCCE.forEach(async element => {
                    this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, recombinedSampleNameVersion, countOfCCEExp[0].title.split(reportConstants.CCEData)[0]);
                });
                /**********************************************************/
                listOfCCE.forEach(exp => {
                    if (exp.cceProperties.isSwelling === false) {
                        this.reportPDFRenderersService.CCE(doc, margin, exp, wellNameVersion, recombinedSampleNameVersion);
                    }
                });

                /*****************************Dew Point*****************************/
                summaryOfCCEDew.forEach(async element => {
                    this.reportPDFRenderersService.summaryofCCE(doc, margin, element, wellNameVersion, recombinedSampleNameVersion, countOfCCEExp[0].title.split(reportConstants.CCEData)[0]);
                });

                cceDewPoint.forEach(exp => {
                    if (exp.cceDewPointProperties.isSwelling === false) {
                        this.reportPDFRenderersService.CCEDewPoint(doc, margin, exp, wellNameVersion, recombinedSampleNameVersion);
                    }
                });


            }

            /*Appendix E- Swelling Test*/
            const countOfSSwellingExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameSwelling);
            if (countOfSSwellingExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, countOfSSwellingExp[0].title.split("-" + reportConstants.swell)[0] + reportConstants.swellingTestSepGasData, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title.includes(countOfSSwellingExp[0].title));
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                swellingExp.forEach(element => {
                    this.reportPDFRenderersService.SwellingReport(doc, margin, element, wellNameVersion, countOfSSwellingExp[0]?.title.split(reportConstants.swellTestDataSepGas)[0]);

                    listOfCCE.forEach(exp => {
                        if (exp.cceProperties.isSwelling === true && exp.cceProperties.swellingSummaryId === element.labRepeatId) {
                            const cceSatP = exp.bpTable.find((x: BPTable) => x.pointName === reportConstants.satP)?.pressure;
                            const temp = exp.cceProperties.studyTemperature;
                            const swellingSatP = (element.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;
                            this.reportPDFRenderersService.CCESepGas(doc, margin, exp, wellName, swellingSatP);
                        }
                    });

                    cceDewPoint.forEach(exp => {
                        if (exp.cceDewPointProperties.isSwelling === true && exp.cceDewPointProperties.swellingSummaryId === element.labRepeatId) {
                            const cceSatP = exp.dpTable.find((x: DPTable) => x.pointName === reportConstants.pSat)?.pressure;
                            const temp = exp.cceDewPointProperties.studyTemperature;
                            const swellingSatP = (element.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;
                            const molPercentage = parseFloat(swellingSatP.toFixed(2));
                            this.reportPDFRenderersService.CCEDewPointSepGas(doc, margin, exp, wellName, molPercentage);
                        }
                    });

                });
            }
            /*Appendix C: Separator Test Material Balance*/
            const countOfMSSTExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameMSST);
            if (countOfMSSTExp.length > 0) {
                const pageHeadings = countOfMSSTExp[0].title;
                this.reportPDFRenderersService.AddCenteredSection(doc, pageHeadings, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === countOfMSSTExp[0].title);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                msstExp.forEach(element => {
                    this.reportPDFRenderersService.SeparatorTest(doc, margin, element, countOfMSSTExp[0].title.split('-' + reportConstants.separatorTests)[0] + ": ", wellNameVersion, recombinedSampleNameVersion);
                    this.reportPDFRenderersService.SeparatorTestAppendix(doc, margin, element);
                });
            }
            /*Appendix D-Diff Lib Material Balance*/
            const countOfDLEExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameDLE);
            if (countOfDLEExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, countOfDLEExp[0].title, subContent, fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title === countOfDLEExp[0].title);
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                dleExp.forEach(element => {
                    this.reportPDFRenderersService.DiffLibTest(doc, margin, element, countOfDLEExp[0].title.split('-' + reportConstants.differentialLiberationMaterialBalance)[0] + ": ", wellNameVersion, recombinedSampleNameVersion);
                    this.reportPDFRenderersService.DiffLibTestAppendix(doc, margin, element);
                });
            }
            /*Appendix D-CVD*/
            const countOfCVDExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameCVD);
            if (countOfCVDExp.length > 0) {
                this.reportPDFRenderersService.AddCenteredSection(doc, countOfCVDExp[0].title.split(reportConstants.cvdExperiment)[0] + reportConstants.constantVolumeDepletion, "", fontSizeTitle, fontSizeSubTitle);
                index = reportContent.tableItem.findIndex(x => x.title.includes(countOfCVDExp[0].title));
                if (index > -1) {
                    reportContent.tableItem[index].page = (doc.getNumberOfPages() + 1).toString();
                }
                cvdExp.forEach(async element => {
                    this.reportPDFRenderersService.CVDStage(doc, margin, element.cvdData.cvdStageResult, countOfCVDExp[0].title.split(reportConstants.cvdExperiment)[0], wellNameVersion, recombinedSampleNameVersion);
                    this.reportPDFRenderersService.CVDOtherPages(doc, margin, element.cvdData, wellNameVersion, recombinedSampleNameVersion);
                });
            }
        }

        /* left side sub header */
        let subHeadder = '';

        const rowsFinal: FinalReportDetailsModel[] = Array.isArray(sampleDetailsExp.finalReportDetails)
            ? sampleDetailsExp.finalReportDetails
            : [sampleDetailsExp.finalReportDetails];

        const newSampleInfoReport = rowsFinal.at(0) ?? null;

        if (reportType === reportConstants.reportTypeDST || reportType === reportConstants.reportTypeMDT) {
            subHeadder =
                reportContent?.wellInformation?.well +
                ' ' +
                reportType.toUpperCase() +
                ' ' +
                reportConstants.samples +
                ' (Perforation Interval :' +
                (newSampleInfoReport?.productionInterval ?? '') +
                ')';
        } else {
            subHeadder =
                reportContent?.wellInformation?.well +
                ' ' +
                reportContent?.wellInformation?.formation +
                ' (Production Interval :' +
                (newSampleInfoReport?.productionInterval ?? '') +
                ')';
        }

        doc.insertPage(3);
        doc.setPage(3);

        this.reportPDFRenderersService.TableOfContents(doc, reportContent.tableItem);

        /* Load logo once as Base64/DataURL */
        const logoDataUrl = await this.getPageLogoDataUrl();
        const logoAlias = 'pageLogo';

        const totalPages = doc.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);

            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;

            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);

            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(
                reportConstants.stabroekBlockConfidential,
                pageWidth / 2,
                pageHeight - 10,
                { align: reportConstants.center }
            );

            doc.text(
                reportConstants.page + ' ' + `${i}`,
                pageWidth - 7,
                pageHeight - 10,
                { align: reportConstants.right }
            );

            if (i >= 3) {
                const logoX = 17;
                const logoY = 9;
                const logoWidth = 40;
                const logoHeight = 8;

                doc.addImage(
                    logoDataUrl,
                    reportConstants.pngCap,
                    logoX,
                    logoY,
                    logoWidth,
                    logoHeight,
                    logoAlias,
                    'FAST'
                );

                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.setFontSize(10);

                doc.text(
                    subHeadder,
                    pageWidth - 10,
                    10,
                    { align: reportConstants.right }
                );
            }
        }


        const fileName = this._reportGeneralUtilityService.BuildReportFileName(
            labTestData,
            reportType,
            reportConstants.pdfExtension
        );

        const pdfArrayBuffer = doc.output(reportConstants.arrayBuffer);

        const pdfBlob = new Blob([pdfArrayBuffer], {
            type: reportConstants.applicationPdf
        });

        const formData = new FormData();
        formData.append(reportConstants.file, pdfBlob, fileName);

        if (preview) {
            return pdfBlob;
        } else {
            doc.save(labTestData.dataLabTest[0].wellName + '_' + labTestData.dataLabTest[0].cylinder + reportConstants.sampleReport + reportType + reportConstants.pdfExtension);
            this.projectService.ReportUpload(formData).pipe(takeUntil(this.destroy$)).subscribe();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    private cachedPageLogoDataUrl?: string;

    private async getPageLogoDataUrl(): Promise<string> {
        if (this.cachedPageLogoDataUrl) {
            return this.cachedPageLogoDataUrl;
        }

        const response = await fetch('/' + reportConstants.pageLogoName, {
            cache: 'force-cache'
        });

        const blob = await response.blob();

        this.cachedPageLogoDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);

            reader.readAsDataURL(blob);
        });

        return this.cachedPageLogoDataUrl;
    }

}
declare module 'jspdf' {
    interface jsPDF {
        lastAutoTable?: {
            finalY: number;
        };
    }
}

